import React, { useEffect, useState } from "react";
import { Col, Container, Row, Alert, ListGroup, Tabs, Tab, Button } from "react-bootstrap";
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useSearchParams } from "react-router-dom";
import Loader from "../../components/widgets/Loader";
import AuthorizationChecker from "../../services/AuthorizationChecker.js";
import './ActivityPage.scss';
import ActivitiesContentPage from "./ActivitiesContentPage.jsx";
import Webapp2UsersStats from "../../components/clients/ClientsDetail/ClientDetailTabs/ClientWebapp2TabComponent/Webapp2UsersStats.jsx";
import Webapp2Groups from "../../components/clients/ClientsDetail/ClientDetailTabs/ClientWebapp2TabComponent/Webapp2Groups.jsx";
import useSearchWithParams from "../../hooks/useSearchWithParams.jsx";
import HelperMetiers360 from "../../services/HelpersMetiers360.js";
import { useModal } from "../../hooks/useModal.jsx";
import axios from '../../services/axios'

const ActivityPage = () => {

    const { fetchClientById, fetchSubscriptionsByClientId } = useStoreActions(actions => actions.clients);
    const { clientById } = useStoreState(state => state.clients);
    const { fetchWebapp2Counts } = useStoreActions(actions => actions.activity);
    const { webapp2Counts } = useStoreState(state => state.activity);
    const { apiData } = useStoreState(state => state.actionSlugs);
    const { client: clientUniqueId } = useStoreState(state => state.auth);

    const { modalComponent, setModalShow, modalData, setModalData, setIsSending } = useModal();

    const [userStats, setUserStats] = useState(null);
    const [groups, setGroups] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [key, setKey] = useState(searchParams.get('tab') || 'activities');
    const [isLoading, setIsLoading] = useState(true);
    const [openCreateGroup, setOpenCreateGroup] = useState(null);

    const searchPlaceholder = key === 'webappGroups'
        ? 'Rechercher par nom ou code de groupe'
        : 'Rechercher par nom';

    const { searchInput, search } = useSearchWithParams({
        placeholder: searchPlaceholder,
        delay: 400,
    });
    const [clientSubscriptions, setClientSubscriptions] = useState();

    useEffect(() => {
        setIsLoading(true);
        if (!clientById || clientById.uniqueId !== clientUniqueId) {
            fetchClientById(clientUniqueId).finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }
        fetchSubscriptionsByClientId(clientUniqueId)
            .then(response => setClientSubscriptions(response))
    }, [clientUniqueId]);

    useEffect(() => {
        webapp2Counts === null && fetchWebapp2Counts();
    }, []);

    useEffect(() => {
        if (!AuthorizationChecker.isAdmin() && (key === 'webappUsers' || key === 'webappGroups') && clientById) {
            if (!userStats) {
                const getUrl = apiData.ACTIONS?.clients?.getProps?.getWebapp2UsersStats?.url?.replace('uniqueId', clientById.uniqueId);
                axios.get(getUrl)
                    .then(response => setUserStats(response))
            }
            if (!groups) {
                const getUrl = apiData.ACTIONS?.clients?.getProps?.getWebapp2Groups?.url?.replace('uniqueId', clientById.uniqueId);
                axios.get(getUrl)
                    .then(response => setGroups(response))
            }
        }
    }, [key, clientById]);

    const dateNow = new Date();
    const allSubscriptionsExpired = clientSubscriptions?.length > 0
        ? clientSubscriptions.every((subscription) => new Date(subscription.subscriptionEndDate) < dateNow)
        : null;
    const alertEndDateSubscription = allSubscriptionsExpired &&
        <Alert className="mt-3" variant="warning">
            {clientSubscriptions?.length === 1 ? "Votre abonnement est arrivé" : "Tous vos abonnements sont arrivés"} à expiration. <br />
            Aucune activité n'est accessible dans votre webapp.
        </Alert>;

    return <Container fluid>
        <Row><Col><h2>Webapp</h2></Col></Row>
        {alertEndDateSubscription}
        {clientById?.webappAccesses?.length > 0 ?
            <Row>
                    <p className="mb-0">
                    La <b>webapp Métiers 360</b> est un outil complémentaire aux casques de réalité virtuelle pour aller plus loin dans
                        votre expérience. C’est l’occasion, pour vous, de proposer une nouvelle forme d’<b>ateliers courts, interactifs et
                            faciles</b> à mettre en place !
                        <br /><br />
                        Elle contient :
                    </p>
                        <ul>
                            <li>
                            des <b>activités</b> visant à guider les utilisateur⋅rices vers le choix d’une
                                expérience immersive à vivre dans le casque VR
                            </li>
                            <li>le <b>catalogue d’expériences immersives</b> Métiers 360 via leur code d’accès</li>
                            <li>
                                les <b>stories</b> (anecdotes métiers sous différents formats : vidéos 360, histoires inspirantes,
                                brèves d’actualités et références à la culture pop), déjà présentes dans l’ancienne version de
                            la webapp
                            </li>
                        </ul>
                    <Alert variant="info">
                    2 façons pour permettre à vos bénéficiaires d'accéder à la webapp :
                        <ListGroup className="webapp-container-instructions mt-1">
                            <ListGroup.Item>
                                <li>
                                    Partagez directement le lien :&nbsp;
                                    <a href={`${process.env.REACT_APP_WEBAPP2_URL}/direct-access/${clientById?.webappAccesses[0]}`} target="blank" >
                                        {`${process.env.REACT_APP_WEBAPP2_URL}/direct-access/${clientById?.webappAccesses[0]}`}</a>
                                </li>
                            </ListGroup.Item>
                            ou
                            <ListGroup.Item className="pt-0">
                                <li>
                                    Partagez le code de votre établissement : <strong>{clientById?.webappAccesses[0]}</strong>&nbsp;
                                ainsi que le lien : <a href={`${process.env.REACT_APP_WEBAPP2_URL}`} target="blank" >{`${process.env.REACT_APP_WEBAPP2_URL}`}/</a>
                                </li>
                            </ListGroup.Item>
                        </ListGroup>
                    </Alert>
            </Row>
            : null}

        <Row>
            <Col>
                {AuthorizationChecker.isAdmin() ?
                    <h3 className="activity-header">
                        Activités

                        {!!webapp2Counts ?
                            <div className="webapp2Counts">
                                <b>Nombre d'établissements :</b> {webapp2Counts?.webapp2ClientsCount} - <b>Nombre d'utilisateur⋅rices: </b> {webapp2Counts?.webapp2UsersCount}
                            </div>
                            : <Loader />}
                    </h3>
                    : <>
                        {clientById?.storiesViewCount >= 0
                            ? <div className="webappStoriesCount">
                                <b>Stories vues :</b>&nbsp;{clientById?.storiesViewCount}
                            </div>
                            : null}
                        <Tabs defaultActiveKey="activities" id="webapp-activities-tab" activeKey={key} onSelect={(k) => {
                            setKey(k);
                            setSearchParams(prev => {
                                const p = Object.fromEntries(prev.entries());
                                p.tab = k;
                                delete p.subTab;
                                return p;
                            });
                        }} className="d-flex">
                        <Tab eventKey="activities" title="Activités">
                            <ActivitiesContentPage
                                clientById={clientById}
                                isLoading={isLoading}
                            />
                        </Tab>
                        <Tab eventKey="webappUsers" title="Utilisateur⋅rices">
                            <Webapp2UsersStats
                                userStats={userStats}
                                setUserStats={setUserStats}
                                setModalData={setModalData}
                                modalData={modalData}
                                setModalShow={setModalShow}
                                setIsSending={setIsSending}
                                clientById={clientById}
                                groups={groups}
                                setGroups={setGroups}
                            />
                        </Tab>
                        <Tab eventKey="webappGroups" title="Groupes">
                            <Row className="item-page mb-2 align-items-center">
                                <Col>{searchInput}</Col>
                                <Col className="col-auto d-flex align-items-center gap-2">
                                    {openCreateGroup &&
                                        <Button variant="success" size="sm" onClick={openCreateGroup}>
                                            <i className="fas fa-plus"></i>&nbsp;Nouveau
                                        </Button>
                                    }
                                </Col>
                            </Row>
                            <Webapp2Groups
                                groups={groups?.length > 0
                                    ? [...groups]?.filter(g =>
                                        HelperMetiers360.isSearchInText(search, g.name)
                                        || HelperMetiers360.isSearchInText(search, g.code)
                                    )
                                    : groups}
                                setGroups={setGroups}
                                userStats={userStats}
                                setUserStats={setUserStats}
                                setModalData={setModalData}
                                modalData={modalData}
                                setModalShow={setModalShow}
                                setIsSending={setIsSending}
                                clientById={clientById}
                                onCreateReady={(fn) => setOpenCreateGroup(() => fn)}
                            />
                        </Tab>
                    </Tabs>
                    </>}
            </Col>
            {AuthorizationChecker.isAdmin() ? <ActivitiesContentPage /> : null}
            {modalComponent}
        </Row>
        <Row><small>La webapp a fait peau neuve mais si vous souhaitez continuer à utiliser l’ancienne version pour les semaines à venir, 
            elle est encore accessible à cette adresse : 
            <a href={`https://webapp-v1.metiers360.com/direct-access/${clientById?.webappAccesses[0]}`} target="blank" >
                                        {`https://webapp-v1.metiers360.com/direct-access/${clientById?.webappAccesses[0]}`}</a>
            </small></Row>
    </Container>
}

export default ActivityPage;